<template>
  <div class="real-positions">
    <div class="page-toolbar">
      <div>
        <h3>已确认的持仓快照</h3>
        <p>数量、成本、现金与盈亏均来自截图时点，账户后续交易不会自动同步。</p>
      </div>
      <el-button size="small" icon="el-icon-refresh" :loading="loadingHistory || loadingSnapshot" @click="refreshData">刷新快照</el-button>
    </div>
    <el-alert title="只读查看已确认记录。新增或更正快照，请在聊天中提供截图，完成识别、修正和明确确认。" type="info" :closable="false" show-icon />
    <PositionContextPanel />

    <el-alert v-if="historyError" class="state-message" title="快照历史读取失败" :description="historyError" type="error" :closable="false" show-icon />
    <div v-else-if="loadingHistory" class="empty-state" role="status">正在读取快照历史…</div>
    <div v-else-if="historyLoaded && !history.length" class="empty-state">
      <h4>尚无已确认快照</h4>
      <p>没有快照不代表账户空仓。完成截图确认后，可在这里查看。</p>
    </div>
    <template v-else-if="historyLoaded">
      <div class="snapshot-layout">
        <el-card shadow="never" class="history-card">
          <div slot="header"><strong>快照历史</strong><span class="muted"> 最近 {{ historyLimit }} 条以内</span></div>
          <p class="history-note">按截图时间倒序。点击记录查看对应时点的持仓。</p>
          <div class="history-list">
            <button v-for="row in history" :key="row.snapshot_id" class="history-entry" :class="{ selected: selectedId === row.snapshot_id }" :aria-pressed="selectedId === row.snapshot_id" :disabled="row.verification_status !== 'confirmed'" @click="selectSnapshot(row)">
              <span class="history-title">{{ row.snapshot_id === latestId ? '最新确认' : '历史快照' }} · {{ row.positions_count }} 项持仓</span>
              <span>{{ formatTime(row.snapshot_as_of) }}</span>
              <span>{{ row.account_alias || '未记录账户别名' }} · {{ row.verification_status === 'confirmed' ? '已确认' : '未确认' }}</span>
              <span class="long-id">{{ row.snapshot_id }}</span>
            </button>
          </div>
          <p v-if="history.length === historyLimit" class="history-note">已达到本次读取上限，列表不代表全部历史。</p>
        </el-card>

        <div class="detail-column">
          <div v-if="loadingSnapshot" class="empty-state" role="status">正在校验所选快照…</div>
          <el-alert v-else-if="detailError" title="所选快照读取失败" :description="detailError" type="error" :closable="false" show-icon />
          <div v-else-if="!detail" class="empty-state">本次历史列表中暂无可展示的已确认快照。</div>
          <template v-else>
            <el-card shadow="never">
              <div slot="header" class="detail-heading">
                <strong>{{ selectedId === latestId ? '最新确认快照' : '历史快照详情' }}</strong>
                <el-tag size="small" :type="selectedId === latestId ? 'info' : 'warning'">{{ selectedId === latestId ? '截图时点记录' : '正在查看历史' }}</el-tag>
              </div>
              <div class="snapshot-meta">
                <div><span class="muted">账户别名</span><strong>{{ snapshot.account_alias || '未记录' }}</strong></div>
                <div><span class="muted">截图时间</span><strong>{{ formatTime(snapshot.snapshot_as_of) }}</strong></div>
                <div class="wide"><span class="muted">完整快照编号</span><span class="long-id">{{ snapshot.snapshot_id }}</span></div>
              </div>
              <div class="freshness-strip">
                <el-tag size="small" :type="freshness.type">{{ freshness.label }}</el-tag>
                <span>本次读取时距截图：{{ freshness.age }}</span>
                <p>这里展示时间差；交易日、收盘有效期和截图之后的交易尚未核验。需要当前持仓时，请更新截图。</p>
              </div>
              <div class="metrics-grid">
                <div><span>持仓项目</span><strong>{{ snapshot.positions_count }}</strong></div>
                <div><span>可用资金（元）</span><strong>{{ formatNumber(snapshot.available_cash) }}</strong></div>
                <div><span>持仓市值（元）</span><strong>{{ formatNumber(snapshot.total_market_value) }}</strong></div>
                <div><span>浮动盈亏（元）</span><strong>{{ formatNumber(snapshot.total_unrealized_pnl) }}</strong></div>
              </div>
              <p class="history-note">金额为截图记录，缺失值显示“—”，不补零、不更新为实时行情。</p>
            </el-card>

            <el-card shadow="never" class="positions-card">
              <div slot="header"><strong>快照持仓明细</strong><span class="muted"> 已确认 · 明细校验通过</span></div>
              <div v-if="snapshot.positions_count === 0" class="empty-state confirmed-empty">
                <h4>已确认空仓</h4><p>该时点的已确认快照包含 0 项持仓，旧快照仍可在历史中查看。</p>
              </div>
              <el-table v-else :data="detail.positions" row-key="row_id" size="small" border>
                <el-table-column prop="ts_code" label="股票代码" min-width="112" />
                <el-table-column prop="name" label="名称" min-width="105" />
                <el-table-column label="持仓数量" min-width="105" align="right"><template slot-scope="scope">{{ formatNumber(scope.row.quantity, 0) }}</template></el-table-column>
                <el-table-column label="可用数量" min-width="105" align="right"><template slot-scope="scope">{{ formatNumber(scope.row.available_qty, 0) }}</template></el-table-column>
                <el-table-column label="成本价" min-width="110" align="right"><template slot-scope="scope">{{ formatNumber(scope.row.cost_price, 4) }}</template></el-table-column>
                <el-table-column label="截图价格" min-width="110" align="right"><template slot-scope="scope">{{ formatNumber(scope.row.last_price, 4) }}</template></el-table-column>
                <el-table-column label="市值（元）" min-width="125" align="right"><template slot-scope="scope">{{ formatNumber(scope.row.market_value) }}</template></el-table-column>
                <el-table-column label="浮动盈亏（元）" min-width="140" align="right"><template slot-scope="scope">{{ formatNumber(scope.row.unrealized_pnl) }}</template></el-table-column>
                <el-table-column label="盈亏比例" min-width="110" align="right"><template slot-scope="scope">{{ percent(scope.row.pnl_ratio) }}</template></el-table-column>
                <el-table-column type="expand"><template slot-scope="scope"><p class="long-id">完整明细编号：{{ scope.row.row_id }}</p><p>备注：{{ scope.row.position_note || '无' }}</p></template></el-table-column>
              </el-table>
              <details class="evidence-details">
                <summary>查看确认记录与来源</summary>
                <p>来源：券商截图；审阅状态：{{ snapshot.ocr_status || '未记录' }}</p>
                <p class="long-id">截图证据编号：{{ snapshot.evidence_id || '未记录' }}</p>
                <p>入库时间：{{ formatTime(snapshot.created_at) }}</p>
                <p class="long-id">来源摘要：{{ snapshot.source_payload_digest || '未记录' }}</p>
              </details>
            </el-card>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import PositionContextPanel from '@/components/PositionContextPanel.vue'
import { realPositionsApi, HISTORY_LIMIT, realPositionsError, formatTime, formatNumber, numericValue, snapshotFreshness } from '@/api/realPositions'

export default {
  name: 'RealPositions',
  components: { PositionContextPanel },
  data() {
    return {
      history: [], historyLimit: HISTORY_LIMIT, historyLoaded: false, historyError: '',
      detail: null, detailError: '', selectedId: '', latestId: '',
      loadingHistory: false, loadingSnapshot: false, generation: 0, selectionTicket: 0, readAt: Date.now()
    }
  },
  computed: {
    snapshot() { return this.detail ? this.detail.snapshot : {} },
    freshness() { return snapshotFreshness(this.snapshot.snapshot_as_of, this.readAt) }
  },
  created() { this.refreshData() },
  beforeDestroy() { this.generation++; this.selectionTicket++ },
  methods: {
    formatTime,
    formatNumber,
    percent(value) { const number = numericValue(value); return number === null ? '—' : formatNumber(number * 100) + '%' },
    async refreshData() {
      const generation = ++this.generation
      this.selectionTicket++
      this.history = []
      this.historyLoaded = false
      this.detail = null
      this.selectedId = this.latestId = ''
      this.historyError = this.detailError = ''
      this.loadingHistory = true
      this.loadingSnapshot = false
      try {
        const result = await realPositionsApi.history()
        if (generation !== this.generation) return
        this.history = result.items
        this.historyLoaded = true
        const latest = this.history.find(row => row.verification_status === 'confirmed')
        this.latestId = latest ? latest.snapshot_id : ''
        if (latest) await this.selectSnapshot(latest)
      } catch (error) {
        if (generation === this.generation) this.historyError = realPositionsError(error)
      } finally {
        if (generation === this.generation) this.loadingHistory = false
      }
    },
    async selectSnapshot(row) {
      const ticket = ++this.selectionTicket
      const generation = this.generation
      this.selectedId = row.snapshot_id
      this.detail = null
      this.detailError = ''
      this.loadingSnapshot = true
      try {
        const result = await realPositionsApi.snapshot(row)
        if (ticket !== this.selectionTicket || generation !== this.generation) return
        this.detail = result
        this.readAt = Date.now()
      } catch (error) {
        if (ticket === this.selectionTicket && generation === this.generation) this.detailError = realPositionsError(error)
      } finally {
        if (ticket === this.selectionTicket && generation === this.generation) this.loadingSnapshot = false
      }
    }
  }
}
</script>

<style scoped>
.real-positions { color: #334155; }
.page-toolbar, .detail-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.page-toolbar { margin-bottom: 18px; }
.page-toolbar h3 { margin: 0 0 7px; font-size: 20px; color: #172b4d; }
.page-toolbar p, .history-note { margin: 0; color: #64748b; line-height: 1.7; font-size: 13px; }
.snapshot-layout { display: grid; grid-template-columns: 285px minmax(0, 1fr); gap: 18px; margin-top: 18px; align-items: start; }
.detail-column { min-width: 0; }
.history-list { max-height: 640px; overflow-y: auto; margin-top: 14px; }
.history-entry { display: flex; flex-direction: column; gap: 8px; width: 100%; text-align: left; background: #f8fafc; color: #526071; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; margin-bottom: 10px; line-height: 1.5; cursor: pointer; }
.history-entry.selected { background: #eff6ff; border-color: #60a5fa; }
.history-entry:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
.history-entry:disabled { cursor: default; opacity: .7; }
.history-title { font-weight: 600; color: #1e3a5f; }
.muted { color: #64748b; font-size: 12px; font-weight: normal; }
.long-id { overflow-wrap: anywhere; word-break: break-all; font-family: monospace; font-size: 12px; line-height: 1.7; }
.snapshot-meta { display: grid; grid-template-columns: 1fr 1.5fr; gap: 16px; }
.snapshot-meta > div { display: flex; flex-direction: column; gap: 6px; }
.snapshot-meta .wide { grid-column: 1 / -1; }
.snapshot-meta strong { font-size: 14px; font-weight: 500; }
.freshness-strip { margin: 18px 0; padding: 12px; background: #f8fafc; border-radius: 7px; font-size: 13px; line-height: 1.8; }
.freshness-strip > span { margin-right: 10px; }
.freshness-strip p { margin: 6px 0 0; color: #64748b; }
.metrics-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 12px; }
.metrics-grid > div { padding: 12px; border: 1px solid #e2e8f0; border-radius: 7px; }
.metrics-grid span { display: block; font-size: 12px; color: #64748b; margin-bottom: 8px; }
.metrics-grid strong { font-size: 19px; font-weight: 600; overflow-wrap: anywhere; }
.positions-card { margin-top: 18px; }
.empty-state { padding: 45px 20px; background: #fff; text-align: center; color: #64748b; line-height: 1.8; border-radius: 8px; }
.empty-state h4 { color: #334155; margin: 0 0 8px; font-size: 17px; }
.empty-state p { margin: 0; font-size: 13px; }
.state-message { margin-top: 18px; }
.confirmed-empty { background: #f8fafc; }
.evidence-details { margin-top: 18px; padding-top: 14px; border-top: 1px solid #e2e8f0; font-size: 13px; }
.evidence-details summary { cursor: pointer; color: #475569; }
@media (max-width: 1250px) { .snapshot-layout { grid-template-columns: 245px minmax(0, 1fr); } .metrics-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 900px) { .snapshot-layout { grid-template-columns: minmax(0, 1fr); } .history-list { max-height: 250px; } .snapshot-meta { grid-template-columns: 1fr; } .page-toolbar { align-items: flex-start; } }
</style>
